import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  noIndex?: boolean;
}

const BASE_URL = 'https://careerplusrecruitmentservices.com';
const DEFAULT_TITLE = 'Career Plus Recruitment Services';
const DEFAULT_DESCRIPTION = 'Find faculty positions, academic officer roles, and technology jobs at leading educational institutions.';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;

export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  canonicalUrl,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  noIndex = false,
}: SEOProps = {}) {
  useEffect(() => {
    // Update title
    const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    document.title = fullTitle;

    // Helper to update or create meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Update meta description
    setMetaTag('description', description);
    
    // Update keywords if provided
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Update robots
    setMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Update Open Graph tags
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:image', ogImage, true);
    
    if (canonicalUrl) {
      setMetaTag('og:url', `${BASE_URL}${canonicalUrl}`, true);
      
      // Update canonical link
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `${BASE_URL}${canonicalUrl}`;
    }

    // Update Twitter tags
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noIndex]);
}

// Pre-defined SEO configs for common pages
export const SEO_CONFIGS = {
  home: {
    title: undefined, // Uses default
    description: 'Find faculty positions, academic officer roles, and technology jobs at leading educational institutions. Career Plus connects talented professionals with exciting opportunities.',
    canonicalUrl: '/',
  },
  jobs: {
    title: 'Browse Jobs',
    description: 'Explore faculty positions, academic officer roles, and technology opportunities. Find your perfect role at top educational institutions.',
    keywords: 'faculty jobs, academic jobs, teaching positions, EdTech careers',
    canonicalUrl: '/jobs',
  },
  about: {
    title: 'About Us',
    description: 'Learn about Career Plus Recruitment Services - connecting talented professionals with leading educational institutions since our founding.',
    canonicalUrl: '/about',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with Career Plus Recruitment Services. We\'re here to help you find your next career opportunity.',
    canonicalUrl: '/contact',
  },
  services: {
    title: 'Our Services',
    description: 'Discover our recruitment services for educational institutions and job seekers. From faculty placement to executive search.',
    canonicalUrl: '/services',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'Read our privacy policy to understand how Career Plus Recruitment Services collects, uses, and protects your personal information.',
    canonicalUrl: '/privacy',
  },
  terms: {
    title: 'Terms of Service',
    description: 'Review the terms and conditions for using Career Plus Recruitment Services platform.',
    canonicalUrl: '/terms',
  },
};
